import { HttpClient, HttpHeaders, HttpParams, type HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';

import { ConfigService, SentryLogger } from '@fcom/core';
import { enumValues } from '@fcom/core/utils';
import { snapshot } from '@fcom/rx';
import { type Attachment } from '@fcom/ui-components/components/interfaces';
import { LanguageService } from '@fcom/ui-translate';

import {
  RATING_SCHEME_MAP,
  resolveEuStandardCompensationCashValue,
} from '../components/multi-step-conditional-forms/utils/utils';
import {
  CaseType,
  CategoryMapLevel1,
  CategoryMapLevel2,
  CategoryMapLevel3,
  CompensationTypeOptions,
  FormCategories,
  FormCategoryInputs,
  FormSubCategories,
  FormType,
  MultiStepFormSteps,
  OptionalFeedbackInputs,
  SpecialAssistanceFormFields,
  type BankAccount,
  type CaseAttachmentPayload,
  type CaseCategory,
  type CaseCompany,
  type CaseCreator,
  type CaseCustomer,
  type CaseFormData,
  type CaseGetMessage,
  type CasePassengerDetails,
  type CasePostMessage,
  type CaseSubmissionData,
  type Companion,
  type DelayedBaggageCosts,
  type EuCompensationCosts,
  type FeedbackRating,
  type FileOrigin,
  type FormCase,
  type LinkAttachmentToCase,
  type LinkAttachmentToCaseData,
  type LinkAttachmentToCaseResponse,
  type MainPassenger,
  type RetrieveCase,
  type RetrieveFormData,
  type SpecialAssistance,
} from '../interfaces';
import { CUSTOMER_SUPPORT_LANGUAGES } from '../utils';

type HttpGetOptions = Parameters<HttpClient['get']>[1];
type HttpPostOptions = Parameters<HttpClient['post']>[2];

const collectDocumentReferences = (formData: CaseFormData): string[] => {
  // Companions and possible power of attorney -documents
  const companionAttachments: Attachment[] = formData.addPassengers?.companions?.map((c) => c.attachment?.[0]);

  // Receipts for additional expenses
  const receipts: Attachment[] = formData.additionalExpenses?.attachments || [];

  const euCompensationAttachment: Attachment[] = formData.euCompensation?.attachments || [];
  const attachmentIds: string[] =
    companionAttachments
      ?.concat(receipts, euCompensationAttachment)
      ?.filter((attachment: Attachment): string => attachment?.fileId)
      ?.map((attachment: Attachment): string => attachment.fileId) || [];

  const mainPassengerAttachment: Attachment = formData.addPassengers?.mainPassenger?.attachment?.[0];

  if (mainPassengerAttachment?.fileId) {
    return [...attachmentIds, mainPassengerAttachment.fileId];
  }

  const medaAttachmentFileIds: string[] = (
    formData.specialAssistance?.[SpecialAssistanceFormFields.MEDA_ATTACHMENT] || []
  )
    .map((a: Attachment): string => a.fileId)
    .filter(Boolean);

  return [...attachmentIds, ...medaAttachmentFileIds];
};

const convertToNumber = (str: string): number => {
  const number = parseFloat(str);
  return !isNaN(number) ? number : 0;
};

const formTypeToCaseType = (formType: FormType): CaseType => {
  switch (formType) {
    case FormType.FYI:
      return CaseType.FYI;
    case FormType.MEDA:
      return CaseType.MEDA;
    default:
      return CaseType.FYA;
  }
};

@Injectable()
export class ClaimOrFeedbackService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private sentryLogger: SentryLogger,
    private languageService: LanguageService
  ) {}

  createCase<T>(formValues: CaseFormData): Observable<T | HttpErrorResponse> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerFeedback;

    const body: CaseSubmissionData = this.mapFormData(formValues);
    const headers: HttpHeaders = this.buildHeaders({ 'x-api-key': keyId });
    const options: HttpPostOptions = { headers };

    return this.http.post<T>(`${apiUrl}/cases`, body, options).pipe(
      catchError((error: HttpErrorResponse): Observable<HttpErrorResponse> => {
        this.sentryLogger.warn(error?.message, { error });
        return throwError((): HttpErrorResponse => error);
      })
    );
  }

  fetchCase(formValues: RetrieveFormData): Observable<RetrieveCase> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerFeedback;
    const headers: HttpHeaders = this.buildHeaders({ 'x-api-key': keyId });
    const queryParamName: string =
      this.configService.cfg.serviceForm.customerFeedback?.options?.['lastNameQueryParam'] || 'lastname';

    const params: HttpParams = new HttpParams().set(queryParamName, formValues.retrieveForm.familyName);
    const options: HttpGetOptions = { headers, params };
    return this.http.get<RetrieveCase>(`${apiUrl}/cases/${formValues.retrieveForm.caseNumber}`, options);
  }

  linkAttachmentToCase(formValues: LinkAttachmentToCase): Observable<LinkAttachmentToCaseResponse | HttpErrorResponse> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerFeedback;

    const body: LinkAttachmentToCaseData = {
      attachmentIds: [formValues.attachmentId],
      caseId: formValues.caseId,
      lastName: formValues.lastName,
    };
    const headers: HttpHeaders = this.buildHeaders({ 'x-api-key': keyId });
    const options: HttpPostOptions = { headers };
    const linkAttachmentPath: string =
      this.configService.cfg.serviceForm.customerFeedback?.options?.['linkAttachmentPath'] || 'link-attachment';

    return this.http.post<LinkAttachmentToCaseResponse>(`${apiUrl}/${linkAttachmentPath}`, body, options).pipe(
      catchError((error: HttpErrorResponse): Observable<HttpErrorResponse> => {
        this.sentryLogger.warn(error?.message, { error });
        return throwError((): HttpErrorResponse => error);
      })
    );
  }

  // @TODO: Add support for attachments
  sendMessageToCase(
    caseId: string,
    lastName: string,
    message: string,
    attachmentIds: string[]
  ): Observable<CaseGetMessage | HttpErrorResponse> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerFeedback;

    const body: CasePostMessage = {
      caseId,
      lastName,
      message: {
        content: message,
        attachmentIds,
      },
    };
    const options: HttpPostOptions = {
      headers: this.buildHeaders({ 'x-api-key': keyId }),
    };

    return this.http.post<CaseGetMessage | HttpErrorResponse>(`${apiUrl}/comments`, body, options).pipe(
      catchError((error: HttpErrorResponse): Observable<HttpErrorResponse> => {
        this.sentryLogger.warn(error?.message, { error });
        return throwError((): HttpErrorResponse => error);
      })
    );
  }

  private mapFormData(formValues: CaseFormData): CaseSubmissionData {
    const bookingInformation: CaseFormData[MultiStepFormSteps.BOOKING_INFORMATION] = formValues.bookingInformation;
    const langKeyValue: string = this.languageService.langKeyValue.toUpperCase();
    const countryCode: string = snapshot(this.languageService.countryCode);
    return {
      case: this.omitNullAndUndefined<FormCase>({
        caseType: formTypeToCaseType(formValues.formType),
        caseOrigin: 'portal',
        country: countryCode === 'INT' ? 'GB' : countryCode,
        language: CUSTOMER_SUPPORT_LANGUAGES.includes(langKeyValue) ? langKeyValue : 'EN',
        worldTracerNumber: formValues.bagTracing?.worldTracerNumber || null,
        bookingReference: bookingInformation?.reservationNumber ?? null,
        flightDate: bookingInformation?.flightDate ? bookingInformation.flightDate.toString() : null,
        flightNumber: bookingInformation?.flightNumber ?? null,
        origin: bookingInformation?.origin?.locationCode ?? null,
        destination: bookingInformation?.destination?.locationCode ?? null,
        compensationType: formValues.paymentDetails?.compensationType ?? null,
        creator: formValues.formType === FormType.REPRESENTATIVE ? this.mapCreator(formValues.claimantDetails) : null,
        claimCompany:
          formValues.formType === FormType.CLAIM_COMPANY ? this.mapCompany(formValues.claimantDetails) : null,
        customer: this.mapCustomer(formValues),
        companions: this.mapCompanions(formValues.addPassengers?.companions),
        euCompensationCosts: this.mapCompensation(formValues),
        delayedBaggageCosts: this.mapBaggageCosts(formValues),
        bankAccount: this.mapBankAccount(formValues.paymentDetails),
        category: this.mapCategory(formValues),
        specialAssistance: formValues.specialAssistance
          ? this.omitNullAndUndefined<SpecialAssistance>(formValues.specialAssistance)
          : null,
        freeTextFeedback: this.mapFreeTextFeedback(formValues),
      }),
      attachmentIds: collectDocumentReferences(formValues),
    };
  }

  private mapFreeTextFeedback(formValues: CaseFormData): string {
    return (
      formValues[MultiStepFormSteps.OPTIONAL_FEEDBACK]?.additionalComments ||
      formValues?.euCompensation?.additionalComments ||
      ''
    );
  }

  private buildHeaders(params: Record<string, unknown>): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      ...params,
    });
  }

  private mapCategory(formData: CaseFormData): CaseCategory {
    const firstLevel: string = CategoryMapLevel1[formData.category.formSubCategory];
    const secondCategory: string = formData.secondCategory?.secondCategory || formData.category.formSubCategory;
    const euCompensationRating: FeedbackRating[] = this.mapEUCompensationFeedbackRating(
      formData,
      enumValues(OptionalFeedbackInputs)
    );
    const feedbackRating: FeedbackRating[] = this.mapFeedbackRating(formData, enumValues(OptionalFeedbackInputs));
    const thirdLevel: CaseCategory['child']['children'] = euCompensationRating.length
      ? euCompensationRating
      : feedbackRating;

    // Special case for Flight irregularities
    const secondLevel: string =
      formData.category.formSubCategory === FormSubCategories.DELAYED_OR_CANCELLED && thirdLevel.length === 0
        ? ''
        : (CategoryMapLevel2[secondCategory] ?? '');

    return {
      name: firstLevel,
      child: {
        name: secondLevel,
        children: thirdLevel,
      },
    };
  }

  private mapCustomer(formData: CaseFormData): CaseCustomer | null {
    if (!formData.addPassengers) {
      return null;
    }
    const mainPassenger: MainPassenger = formData.addPassengers.mainPassenger;
    const customerFields: CaseFormData['customerFields'] = formData.customerFields;

    return this.omitNullAndUndefined<CaseCustomer>({
      firstname: mainPassenger?.firstName,
      lastname: mainPassenger?.lastName,
      email: mainPassenger?.email ?? null,
      phone: mainPassenger?.phone ?? null,
      fPlusNumber: customerFields?.memberNumber,
      countryOfResidence: mainPassenger?.country,
    });
  }

  private mapCompany(claimantDetails: CaseFormData[MultiStepFormSteps.CLAIMANT_DETAILS]): CaseCompany {
    return {
      companyType: claimantDetails?.companyType,
      name: claimantDetails?.claimCompanyOther || claimantDetails?.claimCompany,
      email: claimantDetails?.email,
      phone: claimantDetails?.phone,
      reference: claimantDetails?.referenceNumber,
    };
  }

  private mapCreator(claimantDetails: CaseFormData[MultiStepFormSteps.CLAIMANT_DETAILS]): CaseCreator {
    return {
      firstname: claimantDetails?.firstName,
      lastname: claimantDetails?.lastName,
      email: claimantDetails?.email,
      phone: claimantDetails?.phone,
    };
  }

  private mapCompanions(companions: Companion[] = []): CasePassengerDetails[] {
    return companions.map((companion: Companion) => {
      return !companion?.isChild
        ? {
            firstname: companion?.firstName,
            lastname: companion?.lastName,
            email: companion?.email,
            phone: companion?.phone,
          }
        : {
            firstname: companion?.firstName,
            lastname: companion?.lastName,
          };
    });
  }

  mapCompensation(formData: CaseFormData): EuCompensationCosts | null {
    if (FormCategories.BAGGAGE_IRREGULARITIES === formData?.category?.[FormCategoryInputs.FORM_CATEGORY]) {
      // delayed baggage cost is mapped in mapBaggageCosts
      return null;
    }
    const standardCompensation: number = resolveEuStandardCompensationCashValue(
      formData?.euCompensation?.euClaimOption
    );
    const expenses: CaseFormData[MultiStepFormSteps.EU_COMPENSATION] = formData.euCompensation;

    if (!expenses && standardCompensation === 0) {
      return null;
    }

    const compensation: EuCompensationCosts = {
      hotelCosts: convertToNumber(expenses?.hotelCosts),
      mealCosts: convertToNumber(expenses?.mealCosts),
      transportationCosts: convertToNumber(expenses?.transportCosts),
      phoneCosts: convertToNumber(expenses?.phoneCosts),
      additionalExpenses: convertToNumber(expenses?.indirectCosts),
      additionalExpensesDescription: expenses?.indirectCostSpecification ?? '',
      standardCompensation,
    };

    const hasValidCompensation: boolean =
      compensation.hotelCosts > 0 ||
      compensation.mealCosts > 0 ||
      compensation.transportationCosts > 0 ||
      compensation.phoneCosts > 0 ||
      compensation.additionalExpenses > 0 ||
      compensation.standardCompensation > 0;

    return hasValidCompensation ? compensation : null;
  }

  mapBaggageCosts(formData: CaseFormData): DelayedBaggageCosts | null {
    if (FormCategories.FLIGHT_DISRUPTION === formData.category?.[FormCategoryInputs.FORM_CATEGORY]) {
      // flight disruption cost is mapped in mapCompensation
      return null;
    }
    const expenses = formData.additionalExpenses;
    if (!expenses) {
      return null;
    }
    const delayedBaggageCost: DelayedBaggageCosts = {
      bagItemCosts: convertToNumber(expenses?.clothAndCosmeticCosts),
      bagRentalCosts: convertToNumber(expenses?.rentedCost),
      additionalExpenses: convertToNumber(expenses?.indirectCosts),
      additionalExpensesDescription: expenses.indirectCostSpecification ?? '',
    };

    const hasValidDelayedBaggageCost: boolean =
      delayedBaggageCost.bagRentalCosts > 0 ||
      delayedBaggageCost.bagItemCosts > 0 ||
      delayedBaggageCost.additionalExpenses > 0;
    return hasValidDelayedBaggageCost ? delayedBaggageCost : null;
  }

  private mapBankAccount(paymentDetails: CaseFormData[MultiStepFormSteps.PAYMENT_DETAILS]): BankAccount | null {
    if (!paymentDetails || paymentDetails.compensationType !== CompensationTypeOptions.CASH) {
      return null;
    }

    return {
      accountHolder: paymentDetails.accountHolder ?? '',
      accountHolderAddress: paymentDetails.accountHolderAddress ?? '',
      bankName: paymentDetails.bankName ?? '',
      bankAddress: paymentDetails.bankAddress ?? '',
      bankCountry: paymentDetails.country ?? '',
      iban: (paymentDetails.accountNumber || paymentDetails.iban) ?? '',
      bic: paymentDetails.bic ?? '',
      bik: paymentDetails.bik ?? '',
      ncc: paymentDetails.ncc ?? '',
      correspondentBankData: paymentDetails.correspondentBankData ?? '',
      patronymicName: paymentDetails.patronymicName ?? '',
      ifsc: paymentDetails.ifsc ?? '',
      inn: paymentDetails.inn ?? '',
      kpp: paymentDetails.kpp ?? '',
    };
  }

  private mapFeedbackRating(formData: CaseFormData, keys: string[]): FeedbackRating[] {
    const formValues: CaseFormData[MultiStepFormSteps.OPTIONAL_FEEDBACK] =
      formData?.[MultiStepFormSteps.OPTIONAL_FEEDBACK];
    return formValues
      ? keys
          .filter((key: string): null | string => formValues[key] && formValues[`${key}Rate`])
          .map(
            (key: string): FeedbackRating => ({
              name: CategoryMapLevel3[key],
              rating: RATING_SCHEME_MAP[formValues[`${key}Rate`] as string],
            })
          )
      : [];
  }

  mapEUCompensationFeedbackRating(formData: CaseFormData, keys: string[]): FeedbackRating[] {
    const formValues: CaseFormData[MultiStepFormSteps.EU_COMPENSATION] = formData?.[MultiStepFormSteps.EU_COMPENSATION];
    return formValues
      ? keys
          .filter((key: string): null | string => formValues[key] && formValues[`${key}Rate`])
          .map(
            (key: string): FeedbackRating => ({
              name: CategoryMapLevel3[key],
              rating: RATING_SCHEME_MAP[formValues[`${key}Rate`] as string],
            })
          )
      : [];
  }

  public uploadFile<T>(
    file: File,
    origin: FileOrigin,
    salesforceCaseId?: string,
    caseId?: string,
    lastName?: string
  ): Observable<T> {
    return this.getFileAttachment(file, origin, salesforceCaseId, caseId, lastName).pipe(
      mergeMap((attachment: CaseAttachmentPayload): Observable<T> => this.uploadAttachment<T>(attachment))
    );
  }

  public getFileAttachment(
    file: File,
    origin: FileOrigin,
    salesforceCaseId?: string,
    caseId?: string,
    lastName?: string
  ): Observable<CaseAttachmentPayload> {
    return new Observable<CaseAttachmentPayload>((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          const result = reader.result.toString();
          observer.next({
            data: result.substring(result.indexOf(',') + 1),
            filename: file.name,
            origin,
            salesforceCaseId,
            caseId,
            lastName,
          });
          observer.complete();
        } else {
          observer.error();
        }
      };
      reader.onerror = () => {
        observer.error();
      };
      reader.readAsDataURL(file);
    });
  }

  public downloadAttachment(attachmentId: string, hash: string): Observable<Blob> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerCareAttachment;
    const headers: HttpHeaders = new HttpHeaders({ 'x-api-key': keyId });
    return this.http
      .post(
        `${apiUrl}/${attachmentId}`,
        {
          attachmentId,
          hash,
        },
        {
          headers,
          responseType: 'arraybuffer',
          observe: 'response',
        }
      )
      .pipe(
        map((response) => {
          return new Blob([response.body], {
            type: response.headers.get('content-type') || 'text/plain',
          });
        }),
        catchError((error: HttpErrorResponse): Observable<never> => {
          return throwError(() => error);
        })
      );
  }

  private uploadAttachment<T>(attachment: CaseAttachmentPayload): Observable<T> {
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerFeedback;
    const attachmentPath: string =
      this.configService.cfg.serviceForm.customerFeedback?.options?.['attachmentPath'] || 'attachment';

    const httpOptions = {
      headers: this.buildHeaders({ 'x-api-key': keyId }),
    };

    return this.http.post<T>(`${apiUrl}/${attachmentPath}`, attachment, httpOptions).pipe(
      // eslint-disable-next-line rxjs/no-implicit-any-catch
      catchError((err) => {
        this.sentryLogger.warn(err.message, { error: err });
        return throwError(() => err);
      })
    );
  }

  private omitNullAndUndefined<T>(value: T): T {
    return Object.keys(value)
      .filter((key) => value[key] !== null && value[key] !== undefined)
      .reduce((acc, key) => {
        acc[key] = value[key];
        return acc;
      }, {} as T);
  }
}
